import React, { useEffect, useMemo, useState } from 'react';
import AdminTopBar from '../../components/AdminTopBar';
import AdminSidebar from '../../components/AdminSidebar';
import { API_BASE_URL } from '../../config';

const TABS = {
  CATEGORIES: 'categories',
  QUESTIONS: 'questions',
  PREVIEW: 'preview',
  STATS: 'stats',
  MESSAGES: 'messages'
};

const getAdminHeaders = () => {
  const token = localStorage.getItem('adminToken') || localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const toInt = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : 0;
};

export default function AdminFaqManager() {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(TABS.CATEGORIES);
  const [categories, setCategories] = useState([]);
  const [statsSummary, setStatsSummary] = useState(null);
  const [statsQuestions, setStatsQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [contactRequests, setContactRequests] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactReplyDraft, setContactReplyDraft] = useState('');
  const [contactNoteDraft, setContactNoteDraft] = useState('');
  const [contactClosedReason, setContactClosedReason] = useState('answered');

  const [newCategory, setNewCategory] = useState({ key: '', name: '', description: '', orderIndex: 0 });
  const [newItem, setNewItem] = useState({
    categoryId: '',
    question: '',
    answer: '',
    orderIndex: 0,
    publishNow: true,
    videoUrl: '',
    videoAspectRatio: '16:9',
    imageUrl: '',
    mediaAltText: ''
  });
  const [rowOrderDraft, setRowOrderDraft] = useState({});
  const [editingItemId, setEditingItemId] = useState(null);
  const [questionCategoryFilter, setQuestionCategoryFilter] = useState('all');
  const [draggingItemId, setDraggingItemId] = useState(null);
  const [dragOverItemId, setDragOverItemId] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    setError('');
    try {
      const headers = getAdminHeaders();
      const [faqRes, summaryRes, questionsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/admin/faqs`, { headers, credentials: 'include' }),
        fetch(`${API_BASE_URL}/api/admin/faqs/stats/summary`, { headers, credentials: 'include' }),
        fetch(`${API_BASE_URL}/api/admin/faqs/stats/questions`, { headers, credentials: 'include' })
      ]);
      if (!faqRes.ok) throw new Error('faq_list_failed');

      const faqJson = await faqRes.json();
      const summaryJson = summaryRes.ok ? await summaryRes.json() : { summary: null };
      const questionsJson = questionsRes.ok ? await questionsRes.json() : { questions: [] };

      const incoming = Array.isArray(faqJson.categories) ? faqJson.categories : [];
      setCategories(incoming);
      setStatsSummary(summaryJson.summary || null);
      setStatsQuestions(Array.isArray(questionsJson.questions) ? questionsJson.questions : []);
      const contactRes = await fetch(`${API_BASE_URL}/api/admin/faq-contact?status=new&page=1&pageSize=100`, {
        headers,
        credentials: 'include'
      });
      const contactJson = contactRes.ok ? await contactRes.json() : { requests: [] };
      const requests = Array.isArray(contactJson.requests) ? contactJson.requests : [];
      setContactRequests(requests);
      setSelectedContactId((prev) => prev || requests[0]?.id || null);

      const nextDraft = {};
      incoming.forEach((cat) => {
        (cat.items || []).forEach((item) => {
          nextDraft[item.id] = item.orderIndex;
        });
      });
      setRowOrderDraft(nextDraft);
    } catch (_err) {
      setError('FAQ yönetim verileri alınamadı.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const flatItems = useMemo(
    () =>
      categories.flatMap((c) =>
        Array.isArray(c.items)
          ? c.items.map((item) => ({
              ...item,
              categoryName: c.name,
              categoryId: c.id,
              categoryIsActive: c.isActive !== false,
              categoryDeletedAt: c.deletedAt || null
            }))
          : []
      ),
    [categories]
  );

  const filteredFlatItems = useMemo(() => {
    if (questionCategoryFilter === 'all') return flatItems;
    const categoryId = Number(questionCategoryFilter);
    if (!Number.isFinite(categoryId)) return flatItems;
    return flatItems.filter((item) => Number(item.categoryId) === categoryId);
  }, [flatItems, questionCategoryFilter]);

  const selectedQuestionCategory = useMemo(() => {
    if (questionCategoryFilter === 'all') return null;
    const categoryId = Number(questionCategoryFilter);
    if (!Number.isFinite(categoryId)) return null;
    return categories.find((item) => Number(item.id) === categoryId) || null;
  }, [categories, questionCategoryFilter]);

  const canDragReorder = Boolean(selectedQuestionCategory) && !editingItemId;

  const isItemVisibleInSss = (item) => {
    const itemIsActive = item.isActive !== false;
    const itemNotDeleted = !item.deletedAt;
    const itemPublished = item.status === 'published';
    const categoryActive = item.categoryIsActive !== false;
    const categoryNotDeleted = !item.categoryDeletedAt;
    return itemIsActive && itemNotDeleted && itemPublished && categoryActive && categoryNotDeleted;
  };

  const selectedContact = useMemo(
    () => contactRequests.find((item) => item.id === selectedContactId) || null,
    [contactRequests, selectedContactId]
  );

  useEffect(() => {
    setContactReplyDraft(selectedContact?.replyMessage || '');
    setContactNoteDraft(selectedContact?.adminNote || '');
    setContactClosedReason(selectedContact?.closedReason || 'answered');
  }, [selectedContact]);

  useEffect(() => {
    if (editingItemId) return;
    if (!selectedQuestionCategory) return;
    setNewItem((prev) => ({
      ...prev,
      categoryId: String(selectedQuestionCategory.id || '')
    }));
  }, [editingItemId, selectedQuestionCategory]);

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/faq-categories`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAdminHeaders() },
        body: JSON.stringify(newCategory)
      });
      if (!res.ok) throw new Error('create_category_failed');
      setNewCategory({ key: '', name: '', description: '', orderIndex: 0 });
      await fetchAll();
    } catch (_err) {
      window.alert('Kategori eklenemedi.');
    }
  };

  const handleUpdateCategory = async (cat) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/faq-categories/${cat.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAdminHeaders() },
        body: JSON.stringify({
          name: cat.name,
          description: cat.description || '',
          orderIndex: toInt(cat.orderIndex),
          isActive: Boolean(cat.isActive)
        })
      });
      if (!res.ok) throw new Error('update_category_failed');
      await fetchAll();
    } catch (_err) {
      window.alert('Kategori güncellenemedi.');
    }
  };

  const handleSoftDeleteCategory = async (categoryId) => {
    if (!window.confirm('Bu kategoriyi pasife almak istiyor musunuz')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/faq-categories/${categoryId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAdminHeaders()
      });
      if (!res.ok) throw new Error('delete_category_failed');
      await fetchAll();
    } catch (_err) {
      window.alert('Kategori pasife alınamadı.');
    }
  };

  const resetItemForm = () => {
    setEditingItemId(null);
    setNewItem({
      categoryId: selectedQuestionCategory ? String(selectedQuestionCategory.id) : '',
      question: '',
      answer: '',
      orderIndex: 0,
      publishNow: true,
      videoUrl: '',
      videoAspectRatio: '16:9',
      imageUrl: '',
      mediaAltText: ''
    });
  };

  const handleBulkReorder = async (items) => {
    if (!Array.isArray(items) || !items.length) return;
    const payload = items.map((item, index) => ({
      id: item.id,
      orderIndex: index + 1
    }));
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/faqs/reorder`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAdminHeaders() },
        body: JSON.stringify({ items: payload })
      });
      if (!res.ok) throw new Error('bulk_reorder_failed');
      setRowOrderDraft((prev) => {
        const next = { ...prev };
        payload.forEach((item) => {
          next[item.id] = item.orderIndex;
        });
        return next;
      });
      await fetchAll();
    } catch (_err) {
      window.alert('Sıralama güncellenemedi.');
      await fetchAll();
    } finally {
      setDraggingItemId(null);
      setDragOverItemId(null);
    }
  };

  const handleCreateItem = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...newItem,
        categoryId: Number(newItem.categoryId || 0),
        orderIndex: toInt(newItem.orderIndex),
        status: newItem.publishNow ? 'published' : 'draft'
      };
      const isEditMode = Boolean(editingItemId);
      const res = await fetch(
        isEditMode ? `${API_BASE_URL}/api/admin/faqs/${editingItemId}` : `${API_BASE_URL}/api/admin/faqs`,
        {
        method: isEditMode ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAdminHeaders() },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error(isEditMode ? 'update_item_failed' : 'create_item_failed');

      resetItemForm();
      await fetchAll();
    } catch (_err) {
      window.alert(editingItemId ? 'Soru güncellenemedi.' : 'Soru eklenemedi.');
    }
  };

  const handleUpdateItemOrder = async (id) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/faqs/${id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAdminHeaders() },
        body: JSON.stringify({ orderIndex: toInt(rowOrderDraft[id]) })
      });
      if (!res.ok) throw new Error('update_item_order_failed');
      await fetchAll();
    } catch (_err) {
      window.alert('Soru sırası güncellenemedi.');
    }
  };

  const handleItemDragStart = (itemId) => {
    if (!canDragReorder) return;
    setDraggingItemId(itemId);
    setDragOverItemId(itemId);
  };

  const handleItemDragEnter = (itemId) => {
    if (!canDragReorder || !draggingItemId || draggingItemId === itemId) return;
    setDragOverItemId(itemId);
  };

  const handleItemDrop = async (targetItemId) => {
    if (!canDragReorder || !draggingItemId || draggingItemId === targetItemId) {
      setDraggingItemId(null);
      setDragOverItemId(null);
      return;
    }
    const reorderedItems = [...filteredFlatItems];
    const sourceIndex = reorderedItems.findIndex((item) => item.id === draggingItemId);
    const targetIndex = reorderedItems.findIndex((item) => item.id === targetItemId);
    if (sourceIndex < 0 || targetIndex < 0) {
      setDraggingItemId(null);
      setDragOverItemId(null);
      return;
    }
    const [movedItem] = reorderedItems.splice(sourceIndex, 1);
    reorderedItems.splice(targetIndex, 0, movedItem);
    await handleBulkReorder(reorderedItems);
  };

  const quickEditItem = (item) => {
    setEditingItemId(item.id);
    setNewItem({
      categoryId: String(item.categoryId || ''),
      question: item.question || '',
      answer: item.answer || '',
      orderIndex: toInt(item.orderIndex, 0),
      publishNow: item.status === 'published',
      videoUrl: item.videoUrl || '',
      videoAspectRatio: item.videoAspectRatio || '16:9',
      imageUrl: item.imageUrl || '',
      mediaAltText: item.mediaAltText || ''
    });
  };

  const handlePublish = async (id) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/faqs/${id}/publish`, {
        method: 'POST',
        credentials: 'include',
        headers: getAdminHeaders()
      });
      if (!res.ok) throw new Error('publish_failed');
      await fetchAll();
    } catch (_err) {
      window.alert('Yayınlama başarısız.');
    }
  };

  const handleSoftDeleteItem = async (id) => {
    if (!window.confirm('Bu soruyu pasife almak istiyor musunuz')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/faqs/${id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAdminHeaders()
      });
      if (!res.ok) throw new Error('delete_failed');
      await fetchAll();
    } catch (_err) {
      window.alert('Pasife alma başarısız.');
    }
  };

  const refreshContacts = async (statusFilter = 'new') => {
    const headers = getAdminHeaders();
    const res = await fetch(`${API_BASE_URL}/api/admin/faq-contact?status=${statusFilter}&page=1&pageSize=100`, {
      headers,
      credentials: 'include'
    });
    const json = res.ok ? await res.json() : { requests: [] };
    const rows = Array.isArray(json.requests) ? json.requests : [];
    setContactRequests(rows);
    setSelectedContactId(rows[0]?.id || null);
  };

  const markContactInProgress = async (id) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/faq-contact/${id}/in-progress`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAdminHeaders() },
        body: JSON.stringify({ adminNote: contactNoteDraft })
      });
      if (!res.ok) throw new Error('in_progress_failed');
      await refreshContacts('in_progress');
    } catch (_err) {
      window.alert('Talep işleme alınamadı.');
    }
  };

  const replyContact = async (id) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/faq-contact/${id}/reply`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAdminHeaders() },
        body: JSON.stringify({ replyMessage: contactReplyDraft, adminNote: contactNoteDraft })
      });
      if (!res.ok) throw new Error('reply_failed');
      await refreshContacts('in_progress');
    } catch (_err) {
      window.alert('Yanıt kaydedilemedi.');
    }
  };

  const closeContact = async (id) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/faq-contact/${id}/close`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAdminHeaders() },
        body: JSON.stringify({ closedReason: contactClosedReason, adminNote: contactNoteDraft })
      });
      if (!res.ok) throw new Error('close_failed');
      await refreshContacts('closed');
    } catch (_err) {
      window.alert('Talep kapatılamadı.');
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-900 dark:bg-[#0d141c] dark:text-white">
      <AdminSidebar mobileOpen={mobileSidebarOpen} onCloseMobile={() => setMobileSidebarOpen(false)} />
      <main className="flex min-h-screen flex-1 flex-col transition-[margin] duration-300 md:ml-[var(--admin-sidebar-width,18rem)]">
        <AdminTopBar title="FAQ Yönetimi" onToggleMobile={() => setMobileSidebarOpen((v) => !v)} />

        <div className="p-6 md:p-8">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <h1 className="text-2xl font-black text-slate-900 dark:text-white">FAQ Yönetimi</h1>
            <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 dark:border-slate-700 dark:bg-[#0b1327]">
              <button type="button" onClick={() => setActiveTab(TABS.CATEGORIES)} className={`rounded-lg px-3 py-2 text-sm ${activeTab === TABS.CATEGORIES ? 'bg-primary text-white' : 'text-slate-600 dark:text-slate-300'}`}>Kategoriler</button>
              <button type="button" onClick={() => setActiveTab(TABS.QUESTIONS)} className={`rounded-lg px-3 py-2 text-sm ${activeTab === TABS.QUESTIONS ? 'bg-primary text-white' : 'text-slate-600 dark:text-slate-300'}`}>Sorular</button>
              <button type="button" onClick={() => setActiveTab(TABS.PREVIEW)} className={`rounded-lg px-3 py-2 text-sm ${activeTab === TABS.PREVIEW ? 'bg-primary text-white' : 'text-slate-600 dark:text-slate-300'}`}>Yayın Önizleme</button>
              <button type="button" onClick={() => setActiveTab(TABS.STATS)} className={`rounded-lg px-3 py-2 text-sm ${activeTab === TABS.STATS ? 'bg-primary text-white' : 'text-slate-600 dark:text-slate-300'}`}>İstatistik</button>
              <button type="button" onClick={() => setActiveTab(TABS.MESSAGES)} className={`rounded-lg px-3 py-2 text-sm ${activeTab === TABS.MESSAGES ? 'bg-primary text-white' : 'text-slate-600 dark:text-slate-300'}`}>Mesajlar</button>
            </div>
          </div>

          {loading ? <div className="rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-[#0b1327]">Yükleniyor...</div> : null}
          {error ? <div className="rounded-xl border border-rose-300 bg-rose-50 p-5 text-rose-700 dark:border-rose-700 dark:bg-rose-950/30 dark:text-rose-200">{error}</div> : null}

          {!loading && !error && activeTab === TABS.CATEGORIES ? (
            <section className="grid gap-4 lg:grid-cols-2">
              <form onSubmit={handleCreateCategory} className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-[#0b1327]">
                <h2 className="mb-3 text-lg font-bold">Yeni Kategori</h2>
                <input className="mb-2 w-full rounded border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-900" placeholder="key (örn: mutfak_karari)" value={newCategory.key} onChange={(e) => setNewCategory((p) => ({ ...p, key: e.target.value }))} />
                <input className="mb-2 w-full rounded border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-900" placeholder="Kategori adı" value={newCategory.name} onChange={(e) => setNewCategory((p) => ({ ...p, name: e.target.value }))} />
                <input className="mb-2 w-full rounded border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-900" placeholder="Açıklama" value={newCategory.description} onChange={(e) => setNewCategory((p) => ({ ...p, description: e.target.value }))} />
                <input className="mb-3 w-full rounded border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-900" type="number" placeholder="Sıra" value={newCategory.orderIndex} onChange={(e) => setNewCategory((p) => ({ ...p, orderIndex: toInt(e.target.value) }))} />
                <button className="rounded bg-primary px-4 py-2 text-sm font-bold text-white">Kategori Ekle</button>
              </form>

              <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-[#0b1327]">
                <h2 className="mb-3 text-lg font-bold">Kategori Listesi</h2>
                <div className="space-y-2 text-sm">
                  {categories.map((cat) => (
                    <div key={cat.id} className="rounded border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/60">
                      <div className="mb-2 text-xs text-slate-500 dark:text-slate-400">Key: {cat.key}</div>
                      <input className="mb-2 w-full rounded border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-900" value={cat.name || ''} onChange={(e) => setCategories((prev) => prev.map((x) => (x.id === cat.id ? { ...x, name: e.target.value } : x)))} />
                      <input className="mb-2 w-full rounded border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-900" value={cat.description || ''} onChange={(e) => setCategories((prev) => prev.map((x) => (x.id === cat.id ? { ...x, description: e.target.value } : x)))} />
                      <input className="mb-2 w-full rounded border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-900" type="number" value={cat.orderIndex || 0} onChange={(e) => setCategories((prev) => prev.map((x) => (x.id === cat.id ? { ...x, orderIndex: toInt(e.target.value) } : x)))} />
                      <div className="text-xs text-slate-500 dark:text-slate-400">Soru: {Array.isArray(cat.items) ? cat.items.length : 0}</div>
                      <div className="mt-2 flex gap-2">
                        <button type="button" onClick={() => handleUpdateCategory(cat)} className="rounded bg-emerald-600 px-2 py-1 text-xs font-bold text-white">Güncelle</button>
                        <button type="button" onClick={() => handleSoftDeleteCategory(cat.id)} className="rounded bg-rose-700 px-2 py-1 text-xs font-bold text-white">Pasife Al</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          ) : null}

          {!loading && !error && activeTab === TABS.QUESTIONS ? (
            <section className="grid gap-4 lg:grid-cols-2">
              <form onSubmit={handleCreateItem} className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-[#0b1327]">
                <h2 className="mb-3 text-lg font-bold">{editingItemId ? 'Soru Düzenle' : 'Yeni Soru'}</h2>
                {selectedQuestionCategory && !editingItemId ? (
                  <div className="mb-3 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-700 dark:border-sky-900 dark:bg-sky-950/30 dark:text-sky-200">
                    Yeni soru kategorisi otomatik olarak <span className="font-bold">{selectedQuestionCategory.name}</span> seçildi.
                  </div>
                ) : null}
                <select className="mb-2 w-full rounded border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-900" value={newItem.categoryId} onChange={(e) => setNewItem((p) => ({ ...p, categoryId: e.target.value }))}>
                  <option value="">Kategori seçin</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <input className="mb-2 w-full rounded border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-900" placeholder="Soru" value={newItem.question} onChange={(e) => setNewItem((p) => ({ ...p, question: e.target.value }))} />
                <textarea className="mb-2 w-full rounded border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-900" placeholder="Cevap" rows={4} value={newItem.answer} onChange={(e) => setNewItem((p) => ({ ...p, answer: e.target.value }))} />
                <label className="mb-3 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900/60">
                  <input
                    type="checkbox"
                    checked={Boolean(newItem.publishNow)}
                    onChange={(e) => setNewItem((p) => ({ ...p, publishNow: e.target.checked }))}
                  />
                  <span>Kaydedince hemen yayına al</span>
                </label>
                <input className="mb-2 w-full rounded border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-900" placeholder="YouTube URL" value={newItem.videoUrl} onChange={(e) => setNewItem((p) => ({ ...p, videoUrl: e.target.value }))} />
                <select className="mb-2 w-full rounded border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-900" value={newItem.videoAspectRatio} onChange={(e) => setNewItem((p) => ({ ...p, videoAspectRatio: e.target.value }))}>
                  <option value="16:9">16:9</option>
                  <option value="9:16">9:16</option>
                </select>
                <input className="mb-2 w-full rounded border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-900" placeholder="Görsel URL" value={newItem.imageUrl} onChange={(e) => setNewItem((p) => ({ ...p, imageUrl: e.target.value }))} />
                <input className="mb-2 w-full rounded border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-900" placeholder="Medya alt metni" value={newItem.mediaAltText} onChange={(e) => setNewItem((p) => ({ ...p, mediaAltText: e.target.value }))} />
                <input className="mb-3 w-full rounded border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-900" type="number" placeholder="Sıra" value={newItem.orderIndex} onChange={(e) => setNewItem((p) => ({ ...p, orderIndex: toInt(e.target.value) }))} />
                <div className="flex gap-2">
                  <button className="rounded bg-primary px-4 py-2 text-sm font-bold text-white">
                    {editingItemId ? 'Değişiklikleri Kaydet' : 'Soru Ekle'}
                  </button>
                  {editingItemId ? (
                    <button type="button" onClick={resetItemForm} className="rounded border border-slate-300 px-4 py-2 text-sm font-bold text-slate-700 dark:border-slate-600 dark:text-slate-200">
                      İptal
                    </button>
                  ) : null}
                </div>
              </form>

              <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-[#0b1327]">
                <h2 className="mb-3 text-lg font-bold">Soru Listesi</h2>
                <div className="mb-3">
                  <label className="mb-1 block text-xs font-semibold text-slate-500 dark:text-slate-400">Kategori filtresi</label>
                  <select
                    className="w-full rounded border border-slate-200 bg-white p-2 text-sm dark:border-slate-700 dark:bg-slate-900"
                    value={questionCategoryFilter}
                    onChange={(e) => setQuestionCategoryFilter(e.target.value)}
                  >
                    <option value="all">Tüm kategoriler</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
                {selectedQuestionCategory ? (
                  <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200">
                    Sıralamayı sürükle-bırak ile değiştirebilirsin. Sol form da bu kategoriye senkronlandı: {selectedQuestionCategory.name}
                  </div>
                ) : (
                  <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-300">
                    Rahat sıralama için önce bir kategori seç. Tüm kategoriler görünümünde manuel sıra alanı kullanılabilir.
                  </div>
                )}
                <div className="max-h-[60vh] space-y-2 overflow-auto pr-1 text-sm">
                  {filteredFlatItems.map((item) => (
                    <div
                      key={item.id}
                      draggable={canDragReorder}
                      onDragStart={() => handleItemDragStart(item.id)}
                      onDragEnter={() => handleItemDragEnter(item.id)}
                      onDragOver={(e) => {
                        if (!canDragReorder) return;
                        e.preventDefault();
                      }}
                      onDragEnd={() => {
                        setDraggingItemId(null);
                        setDragOverItemId(null);
                      }}
                      onDrop={() => handleItemDrop(item.id)}
                      className={`rounded border p-3 transition ${
                        dragOverItemId === item.id && draggingItemId !== item.id
                          ? 'border-primary bg-primary/5 dark:bg-primary/10'
                          : 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900/60'
                      } ${canDragReorder ? 'cursor-move' : ''}`}
                    >
                      <div className="mb-1 font-semibold">{item.question}</div>
                      <div className="mb-2 text-slate-500 dark:text-slate-400">{item.categoryName} | durum: {item.status}</div>
                      <div className={`mb-2 text-xs font-semibold ${isItemVisibleInSss(item) ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                        {isItemVisibleInSss(item) ? "SSS'de görünür" : "SSS'de görünmez"}
                      </div>
                      {canDragReorder ? (
                        <div className="mb-2 flex items-center justify-between rounded-lg border border-dashed border-slate-300 px-3 py-2 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-300">
                          <span>Konum: {rowOrderDraft[item.id] ?? item.orderIndex ?? 0}</span>
                          <span className="font-semibold">Sürükleyip bırak</span>
                        </div>
                      ) : (
                        <div className="mb-2 flex items-center gap-2">
                          <span className="text-xs text-slate-500 dark:text-slate-400">Sıra</span>
                          <input type="number" className="w-20 rounded border border-slate-200 bg-white p-1.5 dark:border-slate-700 dark:bg-slate-900" value={rowOrderDraft[item.id] ?? item.orderIndex ?? 0} onChange={(e) => setRowOrderDraft((prev) => ({ ...prev, [item.id]: toInt(e.target.value) }))} />
                          <button type="button" onClick={() => handleUpdateItemOrder(item.id)} className="rounded bg-slate-700 px-2 py-1 text-xs font-bold text-white">Sırayı Kaydet</button>
                        </div>
                      )}
                      <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={() => quickEditItem(item)} className="rounded bg-sky-700 px-2 py-1 text-xs font-bold text-white">Düzenle</button>
                        <button type="button" onClick={() => handlePublish(item.id)} className="rounded bg-emerald-600 px-2 py-1 text-xs font-bold text-white">Yayınla</button>
                        <button type="button" onClick={() => handleSoftDeleteItem(item.id)} className="rounded bg-rose-700 px-2 py-1 text-xs font-bold text-white">Pasife Al</button>
                      </div>
                    </div>
                  ))}
                  {!filteredFlatItems.length ? (
                    <div className="rounded border border-dashed border-slate-300 p-3 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
                      Bu filtrede soru bulunamadı.
                    </div>
                  ) : null}
                </div>
              </div>
            </section>
          ) : null}

          {!loading && !error && activeTab === TABS.PREVIEW ? (
            <section className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-[#0b1327]">
              <h2 className="mb-3 text-lg font-bold">Yayında Görünen Önizleme</h2>
              <div className="space-y-4 text-sm">
                {categories.map((cat) => (
                  <div key={cat.id}>
                    <h3 className="mb-2 font-bold text-primary">{cat.name}</h3>
                    <div className="space-y-2">
                      {(cat.items || []).filter((i) => i.status === 'published' && i.isActive).map((item) => (
                        <div key={item.id} className="rounded border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/60">
                          <div className="font-semibold">{item.question}</div>
                          <p className="mt-1 text-slate-600 dark:text-slate-300">{item.answer}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {!loading && !error && activeTab === TABS.STATS ? (
            <section className="space-y-4">
              <div className="grid gap-3 md:grid-cols-4">
                <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-[#0b1327]"><div className="text-xs text-slate-500 dark:text-slate-400">Toplam Evet</div><div className="mt-1 text-2xl font-black">{statsSummary?.totalYes ?? 0}</div></div>
                <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-[#0b1327]"><div className="text-xs text-slate-500 dark:text-slate-400">Toplam Hayır</div><div className="mt-1 text-2xl font-black">{statsSummary?.totalNo ?? 0}</div></div>
                <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-[#0b1327]"><div className="text-xs text-slate-500 dark:text-slate-400">Toplam Oy</div><div className="mt-1 text-2xl font-black">{statsSummary?.totalVotes ?? 0}</div></div>
                <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-[#0b1327]"><div className="text-xs text-slate-500 dark:text-slate-400">Onay Oranı</div><div className="mt-1 text-2xl font-black">%{Math.round((statsSummary?.approvalRate ?? 0) * 100)}</div></div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-[#0b1327]">
                <h2 className="mb-3 text-lg font-bold">Soru Bazlı İstatistikler</h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead className="text-slate-500 dark:text-slate-400">
                      <tr>
                        <th className="px-2 py-2">Soru</th>
                        <th className="px-2 py-2">Evet</th>
                        <th className="px-2 py-2">Hayır</th>
                        <th className="px-2 py-2">Toplam</th>
                        <th className="px-2 py-2">Puan</th>
                        <th className="px-2 py-2">Onay %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statsQuestions.map((row) => (
                        <tr key={row.id} className="border-t border-slate-200 dark:border-slate-800">
                          <td className="px-2 py-2">{row.question}</td>
                          <td className="px-2 py-2">{row.yesCount}</td>
                          <td className="px-2 py-2">{row.noCount}</td>
                          <td className="px-2 py-2">{row.totalVotes}</td>
                          <td className="px-2 py-2">{row.likeScore}</td>
                          <td className="px-2 py-2">%{Math.round((row.approvalRate || 0) * 100)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          ) : null}

          {!loading && !error && activeTab === TABS.MESSAGES ? (
            <section className="grid gap-4 lg:grid-cols-[320px,1fr]">
              <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-[#0b1327]">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-lg font-bold">Mesajlar</h2>
                  <button type="button" onClick={() => refreshContacts('new')} className="rounded border border-slate-300 px-2 py-1 text-xs font-semibold dark:border-slate-600">Yenile</button>
                </div>
                <div className="space-y-2 text-sm">
                  {contactRequests.map((row) => (
                    <button
                      key={row.id}
                      type="button"
                      onClick={() => setSelectedContactId(row.id)}
                      className={`w-full rounded border px-3 py-2 text-left ${
                        selectedContactId === row.id
                          ? 'border-primary bg-primary/10'
                          : 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900/60'
                      }`}
                    >
                      <div className="line-clamp-1 font-semibold">{row.subject}</div>
                      <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        {row.source} | {row.status}
                      </div>
                    </button>
                  ))}
                  {!contactRequests.length ? <div className="text-xs text-slate-500 dark:text-slate-400">Kayıt bulunamadı.</div> : null}
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-[#0b1327]">
                {selectedContact ? (
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-xl font-black">{selectedContact.subject}</h3>
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        Kaynak: {selectedContact.source} | Durum: {selectedContact.status}
                      </p>
                    </div>
                    <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm leading-relaxed dark:border-slate-700 dark:bg-slate-900/60">
                      {selectedContact.message}
                    </div>
                    <div className="grid gap-3 md:grid-cols-2">
                      <div className="text-sm"><span className="font-semibold">Kullanıcı:</span> {selectedContact.user?.name || '-'}</div>
                      <div className="text-sm"><span className="font-semibold">E-posta:</span> {selectedContact.email || selectedContact.user?.email || '-'}</div>
                      <div className="text-sm md:col-span-2"><span className="font-semibold">Bağlı Soru:</span> {selectedContact.faqItem?.question || '-'}</div>
                    </div>

                    <textarea
                      rows={4}
                      className="w-full rounded border border-slate-300 bg-white p-2 text-sm dark:border-slate-700 dark:bg-slate-900"
                      placeholder="Admin notu"
                      value={contactNoteDraft}
                      onChange={(e) => setContactNoteDraft(e.target.value)}
                    />
                    <textarea
                      rows={4}
                      className="w-full rounded border border-slate-300 bg-white p-2 text-sm dark:border-slate-700 dark:bg-slate-900"
                      placeholder="Kullanıcıya yanıt"
                      value={contactReplyDraft}
                      onChange={(e) => setContactReplyDraft(e.target.value)}
                    />

                    <div className="flex flex-wrap items-center gap-2">
                      <button type="button" onClick={() => markContactInProgress(selectedContact.id)} className="rounded bg-amber-600 px-3 py-2 text-xs font-bold text-white">İşleme Al</button>
                      <button type="button" onClick={() => replyContact(selectedContact.id)} className="rounded bg-emerald-600 px-3 py-2 text-xs font-bold text-white">Yanıtı Kaydet</button>
                      <select
                        value={contactClosedReason}
                        onChange={(e) => setContactClosedReason(e.target.value)}
                        className="rounded border border-slate-300 bg-white px-2 py-2 text-xs dark:border-slate-700 dark:bg-slate-900"
                      >
                        <option value="answered">answered</option>
                        <option value="spam">spam</option>
                        <option value="duplicate">duplicate</option>
                        <option value="irrelevant">irrelevant</option>
                      </select>
                      <button type="button" onClick={() => closeContact(selectedContact.id)} className="rounded bg-rose-700 px-3 py-2 text-xs font-bold text-white">Kapat</button>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-slate-500 dark:text-slate-400">Detay için soldan bir talep seçin.</div>
                )}
              </div>
            </section>
          ) : null}
        </div>
      </main>
    </div>
  );
}

